import { TabsProvider, useTabs } from '@mui/base';
import cn from 'classnames';
import { forwardRef } from 'react';

import type { ForwardedRef, ReactElement } from 'react';

import type { TabsProps } from './tabs.types';

import './tabs.scss';

/**
 * The top-level component that wraps the Tabs List and Tab Panel components
 * so that tabs and their panels can communicate with one another.
 *
 * ### Usages
 *
 * ```tsx
 * import { Tabs, TabList, Tab, TabPanel } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return (
 *     <Tabs defaultValue="tab-1">
 *       <TabList>
 *         <Tab value="tab-1">Tab 1</Tab>
 *         <Tab value="tab-2">Tab 2</Tab>
 *         <Tab value="tab-3">Tab 3</Tab>
 *       </TabList>
 *       <TabPanel value="tab-1">Tab 1 content</TabPanel>
 *       <TabPanel value="tab-2">Tab 2 content</TabPanel>
 *       <TabPanel value="tab-3">Tab 3 content</TabPanel>
 *     </Tabs>
 *   );
 * }
 * ```
 */
const Tabs = forwardRef((props: TabsProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
  const {
    children,
    className,
    defaultValue,
    onChange,
    selectionFollowsFocus = false,
    value,
    ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
  } = props;

  const { contextValue } = useTabs({
    defaultValue,
    direction: 'ltr',
    onChange,
    orientation: 'horizontal',
    selectionFollowsFocus,
    value,
  });

  return (
    <TabsProvider value={contextValue}>
      <div {...rest} className={cn('rbui-tabs', className)} ref={forwardedRef}>
        {children}
      </div>
    </TabsProvider>
  );
});

Tabs.displayName = 'Tabs';

export default Tabs;
